import { DAY, YEAR_DAYS } from '@/sim/clock';
import type { Note, Reading } from '@/sim/lore';

/**
 * Putting a place's history into words.
 *
 * The counterpart of `ui/lives.ts`, which does the same for one mortal, and it
 * inherits that file's whole discipline: **the world states, it does not
 * explain.** A burn says what burned and when. It does not say *and so the
 * herds left*, and it never says *a god did this* — `sim/lore.ts` has already
 * made the second impossible by never carrying a hand, and this file must not
 * reintroduce it in prose.
 *
 * Two rules that are worth stating because they are what keep it from becoming
 * a dashboard:
 *
 * **A note is a sentence, not a row.** No percentages, no bars, no
 * "significance: 0.72". The player should be able to read the panel the way
 * they would read a plaque, and the moment it grows a number that has to be
 * compared against another number it has stopped being the world talking and
 * started being a management screen.
 *
 * **Years, never days.** `ui/hud.ts`'s feed counts in days because it reports
 * things that happened this week. A place counts in years because a place's
 * memory is decades long — the shipped century's scars run ten to seventy-one
 * years old — and "day 25,660" is not a thing anybody says about a hillside.
 *
 * Pure formatting over `Note`s. Nothing here reads a simulation, a clock or a
 * store; it takes the reading `sim/lore.ts` produced and returns strings.
 */

/** How the line is inked. `strange` is for what has no explanation (law 10). */
export type Tone = 'plain' | 'grim' | 'strange' | 'yours';

export interface LoreLine {
  /** World-year, for the gutter — the same shape a life's memories print days in. */
  year: number;
  text: string;
  tone: Tone;
}

const yearOf = (t: number): number => Math.floor(t / (DAY * YEAR_DAYS));

/**
 * Small counts as words. The same instinct as `lives.ts`'s `COUNT` table: past
 * a handful nobody counts roofs either, they say how much of a place it was.
 */
export const roofCount = (n: number): string =>
  n <= 1 ? 'a single roof'
  : n === 2 ? 'two roofs'
  : n <= 4 ? 'a few roofs'
  : n <= 9 ? 'a scatter of roofs'
  : 'a good many roofs';

/**
 * How much ground the fire took, from where you are standing.
 *
 * Measured (`tools/lore.ts`) against the shipped century: the ten largest scars
 * read 488–856 m of local spread, while 1,195 of 39,633 burned cells spread
 * under 100 m. So the bands below separate the fires that defined a valley from
 * the ones that licked a slope, which is the only distinction the sentence
 * needs to carry.
 */
export const extent = (spread: number, here = 'this'): string =>
  spread >= 600 ? `the whole of ${here} hillside`
  : spread >= 250 ? `${here} ground and a good way past it`
  : `a piece of ${here} ground`;

/** What has grown back. `world/sere.ts`'s four stages, said aloud. */
export const regrowth: Record<string, string> = {
  bare: 'nothing has come back yet',
  grass: 'there is grass over it now',
  shrub: 'scrub stands where the wood was',
  young: 'the wood that grew back is still young',
};

const ANCHOR_STAGE: Record<string, string> = {
  bare: 'and nobody has found it',
  footpath: 'and feet have worn a way to it',
  offerings: 'and they leave things here',
  cairn: 'and they have raised a cairn over it',
  shelter: 'and they have roofed it',
  shrine: 'and it is a shrine now',
  temple: 'and it is a temple now',
};

/**
 * The Chronicle's durable types, in the voice a place would use about itself
 * rather than the voice the feed uses about the moment.
 *
 * `ui/hud.ts`'s `phrase()` is the present tense of the same events — *"a trail
 * was worn"* as it happens. This is the past tense a hundred years later.
 * Deliberately a second table rather than a shared one: the two are the same
 * facts at different distances, and collapsing them would force one of the two
 * registers to read wrong.
 */
const MARK_TEXT: Partial<Record<string, string>> = {
  fire_burned: 'fire ran through this country',
  thing_promoted: 'something here stopped being ordinary',
  anchor_placed: 'this ground was made to matter',
  sacred_raised: 'they built up the place they keep coming back to',
  structure_completed: 'they raised what they needed here',
  path_formed: 'a way was worn through',
  camp_abandoned: 'a camp was left to the land',
  structure_claimed: 'what was left standing found new keepers',
  band_split: 'a people came apart here',
  band_merged: 'two peoples became one here',
  culture_diverged: 'a people became its own',
  divine_earth: 'the ground itself was moved',
  divine_grasp: 'something was carried off',
  divine_aura: 'this place was good for a while, and nobody could say why',
  omen_answered: 'people came here following a sign',
};

/** Which of those read as wounds, for the inking. `ui/hud.ts`'s own GRIM set. */
const GRIM = new Set(['fire_burned', 'camp_abandoned', 'band_split']);

/**
 * And which are unexplainable in principle — a god's doing, reaching the world
 * as a thing with no author. This is the ONLY place the interface acknowledges
 * that something had no cause anybody can name, and it must never resolve into
 * a hand.
 */
const STRANGE = new Set(['divine_earth', 'divine_grasp', 'divine_aura', 'thing_promoted',
                         'omen_answered']);

/** What a strand left behind, in the world's terms rather than the ledger's. */
export const LEFT: Record<string, string> = {
  promotion: 'something here is not ordinary any more',
  anchor: 'the ground is bound',
  earth: 'the shape of the land is different',
  aura: 'the place was good for a while',
  grasp: 'a thing is somewhere else for good',
  fire: 'it burned',
  burn: 'the burn is still on the ground',
  answer: 'somebody went to look where it pointed',
  becoming: 'somebody is not who they were',
};

/** How many times, without a tally — the same instinct as `lives.ts`. */
const often = (n: number): string =>
  n <= 1 ? 'once' : n === 2 ? 'twice' : n <= 5 ? 'a few times'
  : n <= 20 ? 'many times' : 'over and over';

/** One note, in words. Null where a note has nothing sayable. */
export function say(n: Note): LoreLine | null {
  const year = yearOf(n.at);
  switch (n.kind) {
    case 'burned':
      return { year, tone: 'grim',
               text: `fire took ${extent(n.spread)}, and ${regrowth[n.stage] ?? 'it is coming back'}` };

    // The sentence this whole file exists for. `world/sere.ts` spares a
    // promoted thing through the bare year, so a tree that was already apart
    // when the fire came is genuinely standing in its own scar — and the
    // player found that out by pointing at it, not by being told.
    case 'stood-through-fire':
      return { year, tone: 'strange', text: 'and when the fire came, it did not take this one' };

    case 'apart':
      return { year, tone: 'strange',
               text: n.tended
                 ? `nothing about this one has been ordinary since, and it has been meant ${often(n.acts)}`
                 : `something went wrong with this one, and it never came right` };

    case 'forgetting':
      return { year, tone: 'plain',
               text: n.tended ? 'and the world is beginning to forget it'
                              : 'and the ground is closing over it' };

    case 'settled':
      return { year, tone: 'plain', text: `people made a home here, ${roofCount(n.roofs)} of it` };

    case 'abandoned':
      return { year, tone: 'grim', text: 'nobody has mended anything here since' };

    case 'walked': {
      const what = n.strength > 0.85 ? 'a road runs past here, walked for generations'
        : n.strength > 0.5 ? 'a trail runs past here, and it is well walked'
        : 'a faint way was trodden past here';
      // A path decays with disuse; if it is still strong, somebody is keeping
      // it. Only the silence is worth a clause — a used road says nothing new.
      const quiet = n.strength <= 0.5 ? ', and little goes along it now' : '';
      return { year, tone: 'plain', text: what + quiet };
    }

    // Never "a god came down here". `anchor_placed` reaches every eye in the
    // world as a place that started mattering, and no eye can say why.
    case 'stone':
      return { year, tone: 'strange',
               text: `this ground was made to matter ${ANCHOR_STAGE[n.stage] ?? ''}`.trim() };

    case 'mark': {
      const text = MARK_TEXT[n.type];
      if (!text) return null;
      const tone: Tone = GRIM.has(n.type) ? 'grim'
        : STRANGE.has(n.type) || n.cause === 'unexplained' ? 'strange' : 'plain';
      // "and more than once" rather than a count: the mark folds a whole
      // kilometre of a whole century, so its number is not a number about
      // anything a person standing here could have counted. What IS worth
      // saying is how long it has been going on — `first` against `at`, which
      // is the only span in the whole file a place can honestly report.
      const spanned = yearOf(n.first);
      const again = n.count > 1
        ? (spanned < year ? `, and it has been happening since year ${spanned}` : ', and more than once')
        : '';
      return { year, tone, text: text + again };
    }

    /* -- the god's own register ---------------------------------------- */

    case 'yours': {
      const began = yearOf(n.first);
      return { year, tone: 'yours',
               text: `you laid ${n.verb} on this ${often(n.acts)}` +
                     (began < year ? `, first in year ${began}` : '') };
    }

    case 'endured':
      return { year, tone: 'yours',
               text: n.left.length
                 ? `and it stayed — ${n.left.map((l) => LEFT[l] ?? l).join('; ')}`
                 : 'and something of it stayed' };
  }
}

/**
 * A whole reading, in words. `world` first and `yours` after, never
 * interleaved: the two registers are the wall law 10 draws, and a panel that
 * mixed them would be telling the player that the world knows what they did.
 */
export function saying(r: Reading): { world: LoreLine[]; yours: LoreLine[] } {
  const lines = (ns: Note[]): LoreLine[] =>
    ns.map(say).filter((l): l is LoreLine => l !== null);
  return { world: lines(r.world), yours: lines(r.yours) };
}

/**
 * The one-line version, for the hover — the strongest thing the world has to
 * say about this, or null where it has nothing.
 *
 * "Strongest" is oldest, deliberately. A tree that has been apart from the
 * others for ninety years is a better answer to *why should I care about this
 * one* than a path worn last spring, and the oldest note is the one the player
 * has no other way of finding out.
 */
export const gist = (r: Reading): string | null =>
  r.world.length ? (say(r.world[0])?.text ?? null) : null;
